/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Logger } from '@saviynt/common';

import type { GenericAttributeMapping } from '../../../components/AttributeMapping';
import type { RootState } from '../../../services/serviceTypes';
import {
  clearEntitlementMessages,
  setFormStatus,
  setSavedStepData,
  updateEntitlements,
  updateStepFormData,
} from '../data-store/copilotSlice';
import { buildRemoveAccessCumulativePayload } from '../utils/mappingReplacementUtils';
import { processPayloadByMode } from '../utils/payloadProcessingUtils';

import { useApplicationName } from './index';

const useRemoveAccessLogic = () => {
  const dispatch = useDispatch();

  const [allMappings, setAllMappings] = useState<
    Record<number, GenericAttributeMapping[]>
  >({});
  const [allValidationErrors, setAllValidationErrors] = useState<
    Record<number, string | null>
  >({});

  const entitlements = useSelector(
    (state: RootState) => state.copilot.entitlementIteration.entitlements || []
  );

  const addAccessTemplates = useSelector(
    (state: RootState) => state.copilot.addAccessEntitlementTemplates || []
  );

  const stepFormData = useSelector(
    (state: RootState) => state.copilot.stepFormData
  );

  // Sync mappings from Redux on mount
  useMemo(() => {
    const reduxMappings: Record<number, GenericAttributeMapping[]> = {};

    Object.keys(stepFormData).forEach((key) => {
      if (key.startsWith('RemoveAccess_')) {
        const index = parseInt(key.split('_')[1], 10);
        const data = stepFormData[key] as {
          mappings?: GenericAttributeMapping[];
        };

        if (data.mappings) {
          reduxMappings[index] = data.mappings;
        }
      }
    });

    if (Object.keys(reduxMappings).length > 0) {
      setAllMappings(reduxMappings);
    }
  }, [stepFormData]);

  const removeAccessTemplateData = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    (state: RootState) => state.copilot.parsedTemplates.RemoveAccess || {}
  );

  const isEditMode = useSelector(
    (state: RootState) => state.copilot.ui.mode === 'edit'
  );

  const removeAccessPrompts = useSelector(
    (state: RootState) => state.copilot.removeAccessPrompts || {}
  );

  const applicationName: string = useApplicationName();

  const mandatoryMappings: GenericAttributeMapping[] = useMemo(
    () => [
      {
        id: 'mandatory-1',
        saviyntField: '',
        externalAppField: '',
        saviyntValue: '',
        externalAppValue: '',
        isMandatory: true,
      },
      {
        id: 'mandatory-2',
        saviyntField: '',
        externalAppField: '',
        saviyntValue: '',
        externalAppValue: '',
        isMandatory: true,
      },
    ],
    []
  );

  // Parse template to extract initial mappings (fallback compatibility)
  const parseTemplateToMappings = useCallback(
    (
      template: { httpParams?: string } | undefined
    ): GenericAttributeMapping[] => {
      if (!template?.httpParams) {
        return [];
      }

      try {
        const httpParams = JSON.parse(template.httpParams) as {
          data?: Record<string, unknown>;
        };
        const data = httpParams.data || {};
        const mappings: GenericAttributeMapping[] = [];

        Object.entries(data).forEach(([externalField, saviyntValue], index) => {
          if (
            typeof saviyntValue === 'string' &&
            saviyntValue.includes('${usersObj.')
          ) {
            const match = saviyntValue.match(/\$\{usersObj\.([^}]+)\}/);

            if (match) {
              mappings.push({
                id: `mapping-${index}`,
                saviyntField: `usersObj.${match[1]}`,
                externalAppField: externalField,
                saviyntValue: '',
                externalAppValue: '',
              });
            }
          }
        });

        return mappings;
      } catch (error) {
        return [];
      }
    },
    []
  );

  // Populate entitlements from removeAccessTemplateData in edit mode
  useEffect(() => {
    if (
      isEditMode &&
      entitlements.length === 0 &&
      removeAccessTemplateData?.attributeValue
    ) {
      try {
        const attributeValue = JSON.parse(
          removeAccessTemplateData.attributeValue
        );

        if (Array.isArray(attributeValue?.call)) {
          const templateEntitlements = attributeValue.call.map(
            (attr: { name: unknown }, index: number) => ({
              id: index,
              value: attr.name || `Entitlement ${index + 1}`,
            })
          );

          dispatch(updateEntitlements(templateEntitlements));
        }
      } catch (error) {
        Logger.error('Error parsing RemoveAccess attributeValue:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    entitlements.length,
    removeAccessTemplateData?.attributeValue,
    dispatch,
  ]);

  // Load initial mappings from AddAccess step data or templates
  useEffect(() => {
    entitlements.forEach((_, index) => {
      if (!allMappings[index] || allMappings[index].length === 0) {
        // First try to get from AddAccess step data
        const addAccessStepData = stepFormData[`AddAccess_${index}`];

        if (
          addAccessStepData &&
          addAccessStepData.mappings &&
          Array.isArray(addAccessStepData.mappings) &&
          addAccessStepData.mappings.length > 0
        ) {
          setAllMappings((prev) => ({
            ...prev,
            [index]: addAccessStepData.mappings,
          }));
        } else {
          // Fallback to parsing templates
          const currentTemplate = addAccessTemplates[index] as
            | { name?: string; httpParams?: string }
            | undefined;
          const fallbackMappings = parseTemplateToMappings(currentTemplate);

          if (fallbackMappings.length > 0) {
            setAllMappings((prev) => ({
              ...prev,
              [index]: fallbackMappings,
            }));
          }
        }
      }
    });
  }, [
    entitlements.length,
    stepFormData,
    addAccessTemplates,
    parseTemplateToMappings,
    entitlements,
    allMappings,
    isEditMode,
    removeAccessTemplateData?.attributeValue,
  ]);

  const formatEntitlementsList = useCallback(
    (entitlementsList: { value?: string }[]): string => {
      const entitlementNames = entitlementsList.map(
        (ent) => ent.value ?? 'Unknown'
      );

      if (entitlementNames.length === 0) {
        return '';
      }

      if (entitlementNames.length === 1) {
        return entitlementNames[0];
      }

      const last = entitlementNames[entitlementNames.length - 1];
      const capitalizedLast = last.charAt(0).toUpperCase() + last.slice(1);

      return `${entitlementNames.slice(0, -1).join(', ')} & ${capitalizedLast}`;
    },
    []
  );

  const handleMappingsChange = useCallback(
    (entitlementIndex: number, newMappings: GenericAttributeMapping[]) => {
      setAllMappings((prev) => ({
        ...prev,
        [entitlementIndex]: newMappings,
      }));

      // Store in Redux immediately on change (like AddAccess reference)
      const validMappings = newMappings.filter(
        (mapping) => mapping.saviyntField && mapping.externalAppField
      );

      if (validMappings.length > 0) {
        const mappingObject = validMappings.reduce<Record<string, string>>(
          (acc, mapping) => {
            if (mapping.saviyntField && mapping.externalAppField) {
              acc[String(mapping.externalAppField)] = `\${${String(
                mapping.saviyntField ?? ''
              )}}`;
            }

            return acc;
          },
          {}
        );

        const mappingJson = JSON.stringify(mappingObject)
          .slice(1, -1)
          .replace(/"/g, '\\"');

        const jsonKey = `externalconnattvalue.RemoveAccessJSON`;
        const payload = {
          mappings: validMappings.map((mapping) => ({
            id: mapping.id,
            saviyntField: mapping.saviyntField,
            externalAppField: mapping.externalAppField,
            saviyntValue: mapping.saviyntValue,
            externalAppValue: mapping.externalAppValue,
            isMandatory: mapping.isMandatory,
          })),
          entitlementIndex,
          multipleconnection: false,
          [jsonKey]: `{"${mappingJson}"}`,
        };

        dispatch(
          updateStepFormData({
            stepName: `RemoveAccess_${entitlementIndex}`,
            formData: payload,
          })
        );
      } else {
        // Store empty mappings
        dispatch(
          updateStepFormData({
            stepName: `RemoveAccess_${entitlementIndex}`,
            formData: { mappings: [], entitlementIndex },
          })
        );
      }
    },
    [dispatch]
  );

  const validateAndProcessNext = useCallback(
    (
      entitlementIndex: number,
      template: string,
      onFormSubmit: (...args: any[]) => void
    ) => {
      const currentMappings = allMappings[entitlementIndex] || [];
      const validMappings = currentMappings.filter(
        (mapping) => mapping.saviyntField && mapping.externalAppField
      );

      if (validMappings.length < 2) {
        setAllValidationErrors((prev) => ({
          ...prev,
          [entitlementIndex]:
            'At least two attribute mapping is required to remove access.',
        }));

        return false;
      }

      // Collapse the current form after successful validation
      dispatch(
        setFormStatus({
          stepName: `RemoveAccess_${entitlementIndex}`,
          formStatus: 'collapsedview',
        })
      );

      // Clear messages after current entitlement
      dispatch(
        clearEntitlementMessages({
          entitlementIndex,
          entitlementType: 'removeAccess',
          keepForm: true,
        })
      );

      setAllValidationErrors((prev) => ({
        ...prev,
        [entitlementIndex]: null,
      }));

      // Edit mode: Use utility functions and Redux state
      if (isEditMode) {
        const confirmationPayload = stepFormData.RemoveAccessConfirmation;

        if (confirmationPayload) {
          // Use utility function to process payload with all entitlements and prompts
          const processedPayload = processPayloadByMode(true, {
            payload: confirmationPayload,
            currentIndex: entitlementIndex,
            allPrompts: removeAccessPrompts,
            currentPrompt: null,
            totalEntitlements: entitlements.length,
          });

          onFormSubmit('RemoveAccessConfirmation', processedPayload, true);

          return true;
        }
      }

      // Create mode: Build cumulative payload up to current entitlement
      const payload = buildRemoveAccessCumulativePayload(
        template,
        entitlements,
        stepFormData,
        entitlementIndex,
        removeAccessPrompts
      );

      // Create payload matching stepFormData structure
      const savedMappingObject = currentMappings.reduce<Record<string, string>>(
        (acc, mapping) => {
          if (mapping.saviyntField && mapping.externalAppField) {
            acc[String(mapping.externalAppField)] = `\${${String(
              mapping.saviyntField
            )}}`;
          }

          return acc;
        },
        {}
      );

      const savedMappingJson = JSON.stringify(savedMappingObject)
        .slice(1, -1)
        .replace(/"/g, '\\"');

      const jsonKey = `externalconnattvalue.RemoveAccessJSON`;
      const stepFormPayload = {
        mappings: validMappings.map((mapping) => ({
          id: mapping.id,
          saviyntField: mapping.saviyntField,
          externalAppField: mapping.externalAppField,
          saviyntValue: mapping.saviyntValue,
          externalAppValue: mapping.externalAppValue,
          isMandatory: mapping.isMandatory,
        })),
        entitlementIndex,
        multipleconnection: false,
        [jsonKey]: `{"${savedMappingJson}"}`,
      };

      // Save to savedStepData on successful validation
      dispatch(
        setSavedStepData({
          stepName: `RemoveAccess_${entitlementIndex}`,
          formData: stepFormPayload,
        })
      );

      const currentEntitlement =
        entitlements[entitlementIndex]?.value || 'Unknown';

      onFormSubmit(
        'RemoveAccessConfirmation',
        { entitlementIndex, currentEntitlement, payload },
        true
      );

      return true;
    },
    [
      allMappings,
      entitlements,
      dispatch,
      removeAccessPrompts,
      isEditMode,
      stepFormData,
    ]
  );

  const createDynamicConfig = useCallback(
    (attributesData: any) => {
      // Always return config that allows custom values, regardless of API success/failure
      const baseConfig = {
        saviyntApp: {
          name: 'Saviynt',
          icon: 'SaviyntLogoFilledSeparateColors',
          fields: [],
          allowCustomValues: false,
        },
        externalApp: {
          name: applicationName,
          icon: applicationName,
          fields: [],
          allowCustomValues: true,
        },
      };

      if (!attributesData?.tableArributes) {
        return baseConfig;
      }

      const flattenedAttributes = Object.entries(attributesData.tableArributes)
        .flatMap(([parentKey, attributes]) => {
          if (Array.isArray(attributes)) {
            return attributes.map(
              (attr: any) => `${parentKey}.${String(attr.name)}`
            );
          }

          return [];
        })
        .concat(['password.'])
        .sort((a, b) => {
          const priorityOrder = [
            'usersObj',
            'arstasksObj',
            'usersManagerObj',
            'endpointsObj',
            'entitlementValuesObj',
            'password',
          ];

          const getPriority = (str: string) => {
            const index = priorityOrder.findIndex((prefix) =>
              str.startsWith(prefix)
            );

            return index === -1 ? priorityOrder.length : index;
          };

          const priorityA = getPriority(a);
          const priorityB = getPriority(b);

          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }

          const getAttributeLength = (str: string) => {
            const parts = str.split('.');

            return parts.length > 1 ? parts[1].length : str.length;
          };

          return getAttributeLength(a) - getAttributeLength(b);
        });

      return {
        saviyntApp: {
          name: 'Saviynt',
          icon: 'SaviyntLogoFilledSeparateColors',
          fields: flattenedAttributes,
          allowCustomValues: false,
        },
        externalApp: {
          name: applicationName,
          icon: applicationName,
          fields: [],
          allowCustomValues: true,
        },
      };
    },
    [applicationName]
  );

  // Get initial mappings for a specific entitlement index (centralized)
  const getInitialMappings = useCallback(
    (entitlementIndex: number): GenericAttributeMapping[] => {
      // First check Redux stepFormData for saved mappings
      const removeAccessStepData = stepFormData[
        `RemoveAccess_${entitlementIndex}`
      ] as { mappings?: GenericAttributeMapping[] } | undefined;

      if (
        removeAccessStepData?.mappings &&
        removeAccessStepData.mappings.length > 0
      ) {
        return removeAccessStepData.mappings;
      }

      // Then try hook mappings
      const hookMappings = allMappings[entitlementIndex] || [];

      if (hookMappings.length > 0) {
        return hookMappings;
      }

      // In edit mode, extract from RemoveAccess template data
      if (isEditMode && removeAccessTemplateData?.attributeValue) {
        try {
          const attributeValue = JSON.parse(
            removeAccessTemplateData.attributeValue
          );

          if (
            Array.isArray(attributeValue?.call) &&
            attributeValue.call[entitlementIndex]
          ) {
            const callItem = attributeValue.call[entitlementIndex];

            if (callItem.httpParams) {
              const httpParams = JSON.parse(callItem.httpParams);
              const data = httpParams.data || {};
              const mappings: GenericAttributeMapping[] = [];

              Object.entries(data).forEach(
                ([externalField, saviyntValue], mappingIndex) => {
                  if (
                    typeof saviyntValue === 'string' &&
                    saviyntValue.includes('${') &&
                    externalField !== 'configInfo'
                  ) {
                    const match = saviyntValue.match(/\$\{([^}]+)\}/);

                    if (match) {
                      mappings.push({
                        id: `mapping-${mappingIndex}`,
                        saviyntField: match[1],
                        externalAppField: externalField,
                        saviyntValue: '',
                        externalAppValue: '',
                      });
                    }
                  }
                }
              );

              if (mappings.length > 0) {
                return mappings;
              }
            }
          }
        } catch (error) {
          Logger.error(
            'Error parsing RemoveAccess mappings in getInitialMappings:',
            error
          );
        }
      }

      // Fall back to template parsing
      const currentTemplate = addAccessTemplates[entitlementIndex] as
        | { name?: string; httpParams?: string }
        | undefined;

      return parseTemplateToMappings(currentTemplate);
    },
    [
      stepFormData,
      allMappings,
      addAccessTemplates,
      parseTemplateToMappings,
      isEditMode,
      removeAccessTemplateData?.attributeValue,
    ]
  );

  const handleNext = useCallback(
    (entitlementIndex: number, template: string, onFormSubmit: any) =>
      validateAndProcessNext(entitlementIndex, template, onFormSubmit),
    [validateAndProcessNext]
  );

  return {
    allMappings,
    entitlements,
    allValidationErrors,
    applicationName,
    mandatoryMappings,
    handleMappingsChange,
    validateAndProcessNext,
    createDynamicConfig,
    formatEntitlementsList,
    getInitialMappings,
    handleNext,
  };
};

export default useRemoveAccessLogic;
